/**
 * Thin token-bound client for the separately deployed QCE backend, called from the
 * qceSlice thunks (no RTK Query — see qceSlice.ts). Reuses the user's CMS JWT as Bearer.
 */
import type {
  Theme,
  ThemeWrite,
  ThemeDraft,
  Exemplar,
  ThemeCode,
  ClipAttach,
  HighlightCreate,
  ThemeConfig,
  AutoDetectConfig,
  AutoDetectRunRequest,
  AutoDetectRunResult,
  ReviewStatus,
} from "../../../Pages/Project/Tabs/ThemesTab/types";

// Base origin of the QCE service. Set QCE_API_URL per environment (same build-time
// mechanism as API_BASE_URL). Outside production we fall back to the staging host
// so the feature works out of the box in dev; in production we deliberately do NOT
// fall back — an unset var must fail loudly (relative /qce → 404) rather than
// silently pointing a prod build at the staging backend and cross-writing data.
const QCE_FALLBACK = process.env.NODE_ENV === "production" ? "" : "https://nlp.staging.theirstory.io";
const QCE_BASE = (process.env.QCE_API_URL || QCE_FALLBACK).replace(/\/+$/, "");
// The whole API is mounted under /qce.
const QCE_PREFIX = "/qce";

/**
 * PATCH /api/codes/:id body. `memo`/`rating` are the original fields; the two
 * optional boundary pairs must each be sent together and are the highlight's
 * own units — `start`/`end` playhead seconds, `start_offset`/`end_offset` ms.
 */
/** PATCH /api/codes/:id response: the full updated code; `reanchored` says whether a
 *  boundary change also re-cut the excerpt text (false = playback range only). */
export type UpdatedCode = ThemeCode & { reanchored?: boolean };

/** A failed QCE request. `code` is the HTTP status as a string — it survives RTK's
 *  thunk error serialization (unwrap() rejects with {name, message, code}). */
export class QceRequestError extends Error {
  status: number;

  code: string;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
    this.code = String(status);
  }
}

/** True when a (possibly thunk-serialized) QCE error has this HTTP status. */
export const isQceStatus = (e: unknown, status: number): boolean =>
  String((e as { code?: unknown } | null)?.code) === String(status);

export interface CodePatch {
  memo?: string;
  rating?: number | null;
  start?: number;
  end?: number;
  start_offset?: number;
  end_offset?: number;
  /** AI-added codes only (KS 400s otherwise); null = undo. */
  review_status?: ReviewStatus;
  review_remark?: string;
}

async function request<T>(token: string | undefined, path: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((options.headers as Record<string, string>) || {}),
  };
  if (token) headers.Authorization = token.startsWith("Bearer ") ? token : `Bearer ${token}`;

  const res = await fetch(`${QCE_BASE}${QCE_PREFIX}${path}`, { ...options, headers });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      detail = body?.detail || body?.error || detail;
    } catch {
      /* non-JSON error body */
    }
    throw new QceRequestError(detail, res.status);
  }
  if (res.status === 204) return null as T;
  return (await res.json()) as T;
}

export const qceService = {
  // Pass a projectId to list only that project's themes (KS excludes
  // null-project/legacy rows); omit it for the org-wide list (global tab).
  listThemes: (token?: string, projectId?: string) =>
    request<Theme[]>(token, projectId ? `/api/themes?projectId=${encodeURIComponent(projectId)}` : "/api/themes"),
  createTheme: (token: string | undefined, body: ThemeWrite) =>
    request<Theme>(token, "/api/themes", { method: "POST", body: JSON.stringify(body) }),
  updateTheme: (token: string | undefined, id: string, body: ThemeWrite) =>
    request<Theme>(token, `/api/themes/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  deleteTheme: (token: string | undefined, id: string) =>
    request<{ status: string; id: string }>(token, `/api/themes/${id}`, { method: "DELETE" }),
  generateThemeFromExamples: (token: string | undefined, exemplars: Exemplar[]) =>
    request<ThemeDraft>(token, "/api/themes/generate-from-examples", {
      method: "POST",
      body: JSON.stringify({ exemplars }),
    }),
  // "Not a fit": KS records the quote (+ optional reason) as an exclusion in
  // the theme's skill, so detection learns to skip similar excerpts.
  themeFeedback: (token: string | undefined, themeId: string, body: { quote: string; rationale?: string }) =>
    request<{ theme: Theme; updated: boolean }>(token, `/api/themes/${themeId}/feedback`, {
      method: "POST",
      body: JSON.stringify({ ...body, decision: "reject" }),
    }),
  attachClips: (token: string | undefined, themeId: string, clips: ClipAttach[]) =>
    request<ThemeCode[]>(token, `/api/themes/${themeId}/clips`, { method: "POST", body: JSON.stringify({ clips }) }),
  listThemeCodes: (token: string | undefined, themeId: string) =>
    request<ThemeCode[]>(token, `/api/themes/${themeId}/codes`),
  listStoryCodes: (token: string | undefined, storyId: string) =>
    request<ThemeCode[]>(token, `/api/stories/${storyId}/codes`),
  updateCode: (token: string | undefined, codeId: string, patch: CodePatch) =>
    request<UpdatedCode>(token, `/api/codes/${codeId}`, { method: "PATCH", body: JSON.stringify(patch) }),
  deleteCode: (token: string | undefined, codeId: string) =>
    request<{ status: string; id: string }>(token, `/api/codes/${codeId}`, { method: "DELETE" }),
  attachHighlight: (token: string | undefined, body: HighlightCreate) =>
    request<ThemeCode[]>(token, "/api/highlights", { method: "POST", body: JSON.stringify(body) }),
  updateExcerptNote: (token: string | undefined, excerptId: string, note: string) =>
    request<ThemeCode[]>(token, `/api/excerpts/${excerptId}`, { method: "PATCH", body: JSON.stringify({ note }) }),
  addExcerptTheme: (token: string | undefined, excerptId: string, themeId: string) =>
    request<ThemeCode[]>(token, `/api/excerpts/${excerptId}/themes`, {
      method: "POST",
      body: JSON.stringify({ theme_id: themeId }),
    }),
  // Auto-detection config. Callers default to a disabled config on 404.
  getThemeConfig: (token: string | undefined, projectId: string) =>
    request<ThemeConfig>(token, `/api/projects/${projectId}/theme-config`),
  putThemeConfig: (token: string | undefined, projectId: string, autoDetect: AutoDetectConfig) =>
    request<ThemeConfig>(token, `/api/projects/${projectId}/theme-config`, {
      method: "PUT",
      body: JSON.stringify({ autoDetect }),
    }),
  // Run detection over the project's existing recordings (synchronous, seconds).
  runThemeConfig: (token: string | undefined, projectId: string, body: AutoDetectRunRequest) =>
    request<AutoDetectRunResult>(token, `/api/projects/${projectId}/theme-config/run`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  // Per-user favorites (`qual_favorites`) — org plane, keyed off the JWT `sub`.
  listFavorites: (token?: string) => request<string[]>(token, "/api/favorites"),
  addFavorite: (token: string | undefined, codeId: string) =>
    request<void>(token, "/api/favorites", { method: "POST", body: JSON.stringify({ codeId }) }),
  removeFavorite: (token: string | undefined, codeId: string) =>
    request<void>(token, `/api/favorites/${codeId}`, { method: "DELETE" }),
};

/** Extract a human-readable message from a thrown error. */
export function qceErrMsg(e: any, fallback = "Something went wrong"): string {
  return e?.message || e?.data?.detail || fallback;
}

/**
 * Build a full ThemeWrite payload from an existing theme, applying overrides.
 * Used to persist a single field (e.g. links or notes) via PUT without losing
 * the theme's other fields.
 */
export function themeToWrite(t: Theme, overrides: Partial<ThemeWrite> = {}): ThemeWrite {
  return {
    name: t.name,
    color: t.color,
    description: t.description,
    keywords: t.keywords,
    icon: t.icon,
    exemplars: t.exemplars || [],
    skill: t.skill || "",
    from_skill: false,
    notes: t.notes || "",
    links: t.links || [],
    // Preserve the theme's project assignment across edits.
    projectId: t.projectId,
    ...overrides,
  };
}
